import { SmallAddIcon } from "@chakra-ui/icons";
import {
  Button,
  HStack,
  Link,
  MenuItem,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  getExternalIDsRetrieveQueryKey,
  useExternalIDsRetrieveSuspense,
  useExternalIDsUpsert,
} from "gen/endpoints/external-id/external-id";
import {
  getExternalIDTableDataRetrieveQueryKey,
  useExternalIDTableDataRetrieveSuspense,
} from "gen/endpoints/external-id-table/external-id-table";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { ExternalIDTypeEnum } from "gen/model/externalIDTypeEnum";
import { ExternalIDUpsertAPIDataRequest } from "gen/model/externalIDUpsertAPIDataRequest";
import type { ExternalIDUpsertData } from "gen/model/externalIDUpsertData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { externalIDsUpsertBody } from "gen/schemas/external-id/external-id";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import { FormFieldset } from "shared/components/form/FormFieldset";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActionsMenu } from "shared/components/table/box/TableBoxActionsMenu";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxCollapse } from "shared/components/table/box/TableBoxCollapse";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { MoreHorizontalIcon } from "shared/icons/MoreHorizontalIcon";
import { enumKeys } from "shared/utils/EnumUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const InvestorEntityExternalIdsBlock: React.FC<{
  investorEntityId: string;
}> = ({ investorEntityId }) => {
  const { t } = useTranslation(["investorEntity", "entity", "common"]);

  const { data: externalIdTable } =
    useExternalIDTableDataRetrieveSuspense(investorEntityId);

  const { data: externalIdList } =
    useExternalIDsRetrieveSuspense(investorEntityId);

  const {
    isOpen: isExternalEditDrawerOpen,
    onOpen: onExternalIdDrawerOpen,
    onClose: onExternalIdDrawerClose,
  } = useDisclosure();

  return (
    <>
      <TableBox
        isDefaultExpanded={externalIdTable.rows.length > 0}
        boxProps={{ sx: { mb: "16px" } }}
      >
        <TableBoxHeader>
          <TableBoxTitle textStyle="bodyBold">
            {t("investorEntity:externalIds.title")}
          </TableBoxTitle>
          <DataTableBoxActions
            endChildren={
              <Restricted permission={PermissionTypeEnum.config}>
                <TableBoxActionsMenu IconComponent={MoreHorizontalIcon}>
                  <MenuItem onClick={onExternalIdDrawerOpen}>
                    {t("common:edit")}
                  </MenuItem>
                </TableBoxActionsMenu>
              </Restricted>
            }
          />
        </TableBoxHeader>
        <TableBoxCollapse>
          <TableBoxBody>
            <DataTable
              data={externalIdTable}
              disablePinnedHeaderColumns={true}
              emptyStateProps={{
                title: t("investorEntity:externalIds.empty.title"),
                children: (
                  <Restricted permission={PermissionTypeEnum.config}>
                    <Button
                      colorScheme="secondary"
                      onClick={onExternalIdDrawerOpen}
                    >
                      {t("common:configure")}
                    </Button>
                  </Restricted>
                ),
              }}
            />
          </TableBoxBody>
        </TableBoxCollapse>
      </TableBox>
      {isExternalEditDrawerOpen && (
        <ExternalIdsEditDrawer
          externalIdList={externalIdList}
          onCloseComplete={onExternalIdDrawerClose}
        />
      )}
    </>
  );
};

interface ExternalIdsEditDrawerProps {
  externalIdList: ExternalIDUpsertData[];
  onCloseComplete: () => void;
}

const ExternalIdsEditDrawer: React.FC<ExternalIdsEditDrawerProps> = ({
  externalIdList,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["investorEntity", "enums"]);
  const queryClient = useQueryClient();

  const { investorEntityId } = useRequiredStringParams();

  const { mutateAsync: updateExternalIds } = useExternalIDsUpsert();

  const form = useForm<ExternalIDUpsertAPIDataRequest>({
    resolver: zodResolver(externalIDsUpsertBody),
    defaultValues: {
      upsert_data_list: externalIdList.length
        ? externalIdList
        : [
            {
              name: "",
              external_id_type: "" as ExternalIDTypeEnum,
              fund_id: undefined,
            },
          ],
    },
  });

  const onSubmit = (data: ExternalIDUpsertAPIDataRequest) =>
    updateExternalIds({
      investorEntityId,
      data,
    });

  const onSuccess = async () => {
    await invalidateQueryKeys(
      queryClient,
      getExternalIDTableDataRetrieveQueryKey(investorEntityId),
      getExternalIDsRetrieveQueryKey(investorEntityId),
    );
  };

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "upsert_data_list",
  });

  return (
    <FormDrawer
      title={t("investorEntity:externalIds.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t("investorEntity:externalIds.edit.successMessage"),
      }}
      onCloseComplete={onCloseComplete}
      bodyProps={{
        "data-private": true,
      }}
    >
      {fields.map((_, index) => (
        <ExternalIdFormInputs
          key={index}
          index={index}
          remove={() => remove(index)}
          investorEntityId={investorEntityId}
        />
      ))}
      <Button
        colorScheme="secondary"
        size="sm"
        iconSpacing="2px"
        leftIcon={<SmallAddIcon />}
        onClick={() =>
          append({
            name: "",
            external_id_type: "" as ExternalIDTypeEnum,
            fund_id: undefined,
            investor_entity_id: "",
          })
        }
      >
        {t("investorEntity:externalIds.edit.add")}
      </Button>
    </FormDrawer>
  );
};

const ExternalIdFormInputs: React.FC<{
  index: number;
  remove: () => void;
  investorEntityId: string;
}> = ({ index, remove, investorEntityId }) => {
  const { t } = useTranslation(["investorEntity", "enums"]);
  const { data: funds } = useFundListSuspense();
  return (
    <FormFieldPrefix prefix={`upsert_data_list.${index}`}>
      <FormFieldset border="1px dashed" borderColor="gray.400">
        <FormHiddenInput name="investor_entity_id" value={investorEntityId} />
        <FormTextInput
          name="name"
          title={t("investorEntity:externalIds.edit.externalId")}
        />
        <FormSelect
          title={t("investorEntity:externalIds.edit.type")}
          name="external_id_type"
        >
          {enumKeys(ExternalIDTypeEnum).map((type) => (
            <SelectOption value={type} key={type}>
              {t(`enums:ExternalIDTypeEnum.${type}`)}
            </SelectOption>
          ))}
        </FormSelect>
        <FormSelect
          title={t("investorEntity:externalIds.edit.optionalFund")}
          name="fund_id"
          unsetWhenEmpty={true}
        >
          {funds.map(({ id, legal_name }) => (
            <SelectOption value={id} key={id}>
              {legal_name}
            </SelectOption>
          ))}
        </FormSelect>
        <HStack justifyContent="flex-end">
          <Link size="sm" colorScheme="gray" onClick={() => remove()}>
            {t("investorEntity:externalIds.edit.remove")}
          </Link>
        </HStack>
      </FormFieldset>
    </FormFieldPrefix>
  );
};
